/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";

import { LoaderOverlay } from "@nel-ui/react";

import {
  CommonError,
  FlexOneCard,
  HelpAndSupport,
  Layout,
  OtherFlexCard,
} from "../../components";
import { ERROR_MESSAGE, PAGE_LOADING } from "../../constants";
import { useAppProviderContext } from "../../context/appProvider";
import { isFlexOneAccount } from "../../helpers";
import {
  IAccountSubTypeCode,
  IBlockDetailsCardProps,
  IBlockInformationProps,
  IHeaderProps,
  IHelpAndSupportProps,
} from "../../interfaces";

const GamblingBlock: React.FC<any> = () => {
  const { actions, state } = useAppProviderContext();
  const [isFlexOneCard, setIsFlexOneCard] = useState<boolean | null>(null);
  const [headerProps, setHeaderProps] = useState<IHeaderProps>({
    heading: "",
    subHeading: "",
    infoMessage: "",
  });

  const [cardInfo, setCardInfo] = useState<IBlockDetailsCardProps>({
    cardNumber: "",
    expiryDate: "",
    impulseDelayRemaining: "",
    isDeclineAll: false,
    cardDescription: "",
    investorTypeCode: "",
    cardStaticContent: { cardInfo: {} },
    accountSubTypeCode: "7002" as IAccountSubTypeCode,
  });

  const [helpAndSupportInfo, setHelpAndSupportInfo] =
    useState<IHelpAndSupportProps>({
      heading: "",
      helpSupportUrl: "",
      helpSupportInfo: "",
      helpSupportAdditionsInfo: "",
    });

  const [blockInfo, setBlockInfo] = useState<IBlockInformationProps>({
    howBlockWorks: {},
    howToBlockAccess: {},
    whatDoWeBlock: {},
  });

  const fetchData = useCallback(async () => {
    if (isFlexOneCard) {
      await actions.getJSONContent();
    } else {
      await actions.getGamblingControlWithJson(
        state.startupData?.account[0]?.tokenisedPan as string
      );
    }
  }, [actions, isFlexOneCard]);

  const updateHeaderProps = useCallback(() => {
    if (state.cmsStaticContent?.heading) {
      const commonHeaderProps = {
        heading: state.cmsStaticContent.heading,
        subHeading: isFlexOneCard
          ? state.cmsStaticContent.flexOne.sub_heading
          : state.cmsStaticContent.non_flexOne.sub_heading,
        infoMessage: isFlexOneCard
          ? ""
          : state.cmsStaticContent.non_flexOne.sub_heading_info,
      };
      setHeaderProps(commonHeaderProps);
      if (!isFlexOneCard) {
        setBlockInfo({
          howBlockWorks: { ...state.cmsStaticContent.how_block_works },
          howToBlockAccess: { ...state.cmsStaticContent.how_to_block_access },
          whatDoWeBlock: { ...state.cmsStaticContent.what_do_we_block },
        });
        setCardInfo((prevState) => ({
          ...prevState,
          isDeclineAll: state?.gamblingBlock?.declineAll,
          impulseDelayRemaining: state?.gamblingBlock?.impulseDelayRemaining,
        }));
      }
      setHelpAndSupportInfo({
        heading: state.cmsStaticContent.help_and_support.heading,
        helpSupportUrl:
          state.cmsStaticContent.help_and_support.website_support_url,
        helpSupportInfo:
          state.cmsStaticContent.help_and_support.website_support_info,
        helpSupportAdditionsInfo:
          state.cmsStaticContent.help_and_support.additional_helpful_contacts,
      });
    }
  }, [isFlexOneCard, state.cmsStaticContent]);

  const updateCardInfo = useCallback(() => {
    if (state.cmsStaticContent?.heading && state.startupData?.authToken) {
      setCardInfo((prevState) => ({
        ...prevState,
        ...state?.startupData?.account[0],
        cardNumber: state?.startupData?.account[0].tokenisedPan,
        cardStaticContent: {
          cardInfo: { ...state.cmsStaticContent.card_details },
        },
      }));
    }
  }, [state.cmsStaticContent, state.startupData]);

  useEffect(() => {
    if (state.startupData?.authToken) {
      setIsFlexOneCard(
        !!isFlexOneAccount(
          state.startupData?.account[0]?.accountSubTypeCode as string
        )
      );
    }
  }, [state.startupData]);

  useEffect(() => {
    updateHeaderProps();
  }, [isFlexOneCard, state.cmsStaticContent]);

  useEffect(() => {
    updateCardInfo();
  }, [state.cmsStaticContent, state.startupData]);

  useEffect(() => {
    if (state.startupData?.authToken) {
      void fetchData();
    }
  }, [isFlexOneCard]);

  useEffect(() => {
    setCardInfo((prevState) => ({
      ...prevState,
      isDeclineAll: state?.gamblingBlock?.declineAll,
      impulseDelayRemaining: state?.gamblingBlock?.impulseDelayRemaining,
    }));
  }, [
    state.gamblingBlock?.impulseDelayRemaining,
    state.gamblingBlock?.declineAll,
  ]);

  const renderContent = () => {
    // Show LoadingOverlay component if data is loading
    if (state.isLoading) {
      return (
        <LoaderOverlay
          data-testid="gambling-loader"
          spinnerProps={{ size: "m", ariaLabel: PAGE_LOADING }}
        />
      );
    }

    // Show error message if data fetching fails
    if (state.error) {
      return (
        <CommonError
          heading={ERROR_MESSAGE.HEADING}
          subHeading={ERROR_MESSAGE.SUB_HEADING}
        />
      );
    }

    // Show content if data is loaded successfully
    if (state.cmsStaticContent) {
      // Render FlexOneCard component if account is FlexOne else render OtherFlexCard component
      return (
        <>
          {isFlexOneCard ? (
            <FlexOneCard headerInfo={headerProps} cardInfo={cardInfo} />
          ) : (
            <OtherFlexCard
              headerInfo={headerProps}
              cardInfo={cardInfo}
              blockInfo={blockInfo}
            />
          )}
          <HelpAndSupport {...helpAndSupportInfo} />
        </>
      );
    }

    return null;
  };

  return <Layout>{renderContent()}</Layout>;
};

export default GamblingBlock;
